'use client';

import { useEffect, useRef, useState } from 'react';

import { Carousel, CarouselContent, CarouselItem } from '~/components/ui/carousel/carousel';
import { BrandCard, CollectionSkeleton as BrandSkeleton } from '~/module/collection/components';

import type { EmblaCarouselType } from 'embla-carousel';

interface Brand {
  id: string;
  image?: string | null;
  imagePosition?: 'contain' | 'cover';
  name: string;
}

interface BrandCarouselProps {
  brands?: Brand[];
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  isLoading?: boolean;
  onLoadMore?: () => void;
}

const LOAD_MORE_THRESHOLD = 2;

export const BrandCarousel = ({
  brands,
  isLoading,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: BrandCarouselProps) => {
  const [api, setApi] = useState<EmblaCarouselType | undefined>(undefined);

  const brandsList = brands ?? [];
  const brandPairs: Brand[][] = [];
  for (let index = 0; index < brandsList.length; index += 2) {
    brandPairs.push(brandsList.slice(index, index + 2));
  }

  const brandImages = brandsList.map((brand) => brand.image ?? '').filter(Boolean);

  const stateRef = useRef({ onLoadMore, hasNextPage, isFetchingNextPage });
  stateRef.current = { onLoadMore, hasNextPage, isFetchingNextPage };

  useEffect(() => {
    if (!api) {
      return;
    }

    const checkAndLoad = () => {
      const {
        onLoadMore: currentOnLoadMore,
        hasNextPage: currentHasNextPage,
        isFetchingNextPage: currentFetching,
      } = stateRef.current;

      if (!currentOnLoadMore || !currentHasNextPage || currentFetching) {
        return;
      }

      const selectedIndex = api.selectedScrollSnap();
      const lastIndex = api.scrollSnapList().length - 1;

      if (selectedIndex >= lastIndex - LOAD_MORE_THRESHOLD) {
        currentOnLoadMore();
      }
    };

    api.on('select', checkAndLoad);
    api.on('settle', checkAndLoad);
    api.on('reInit', checkAndLoad);

    return () => {
      api.off('select', checkAndLoad);
      api.off('settle', checkAndLoad);
      api.off('reInit', checkAndLoad);
    };
  }, [api]);

  return (
    <Carousel
      className="relative w-full"
      images={brandImages}
      opts={{
        loop: false,
      }}
      plugins={[]}
      setApi={setApi}
    >
      <CarouselContent className="-ml-4">
        {isLoading ? (
          <>
            {Array.from({ length: 2 }).map((_, index) => (
              <CarouselItem key={`skeleton-${index}`} className="pl-4 basis-full">
                <div className="flex gap-4">
                  <div className="w-1/2">
                    <BrandSkeleton />
                  </div>
                  <div className="w-1/2">
                    <BrandSkeleton />
                  </div>
                </div>
              </CarouselItem>
            ))}
          </>
        ) : (
          <>
            {brandPairs.map((pair, index) => (
              <CarouselItem key={`pair-${index}`} className="pl-4 basis-full">
                <div className="flex gap-4">
                  {pair.map((brand) => (
                    <BrandCard
                      key={brand.id}
                      id={brand.id}
                      image={brand.image ?? ''}
                      imagePosition={brand.imagePosition}
                      name={brand.name}
                      variant="carousel"
                    />
                  ))}
                  {pair.length === 1 ? <div className="w-1/2" /> : null}
                </div>
              </CarouselItem>
            ))}
            {isFetchingNextPage ? (
              <CarouselItem key="loading-more" className="pl-4 basis-full">
                <div className="flex gap-4">
                  <div className="w-1/2">
                    <BrandSkeleton />
                  </div>
                  <div className="w-1/2">
                    <BrandSkeleton />
                  </div>
                </div>
              </CarouselItem>
            ) : null}
          </>
        )}
      </CarouselContent>
    </Carousel>
  );
};
